import { Paging } from '@zdrbm/zdr-interfaces';
import {
    EditableEntity,
    ICreatableEntityCollection,
    IEditableEntity,
    LoadingState,
    LoadingStateBroker,
} from '@zdrbm/zdr-native-tools';

export interface IPageLoaderManager<TItems extends IEditableEntity> extends IEditableEntity {
    items: ICreatableEntityCollection<TItems>;
    loadingState: LoadingStateBroker;
    hasMore(): boolean;
    loadMore(): Promise<void>;
}

export abstract class PageLoaderManager<TItems extends IEditableEntity>
    extends EditableEntity<keyof IPageLoaderManager<TItems>>
    implements IPageLoaderManager<TItems>
{
    loadingState: LoadingStateBroker;
    items: ICreatableEntityCollection<TItems>;
    private hasMoreItems;

    constructor(private pageSize: number) {
        super(undefined);
        this.loadingState = new LoadingStateBroker(this);
        this.items = this.createEntityCollection('items', []);
        this.hasMoreItems = true;
    }

    hasMore(): boolean {
        return this.hasMoreItems;
    }

    abstract fetchItems(paging: Paging): Promise<TItems[]>;

    async loadMore(): Promise<void> {
        if (this.hasMore() && this.loadingState.get() !== LoadingState.LOADING) {
            this.loadingState.setLoading();
            try {
                const paging: Paging = {
                    limit: this.pageSize,
                    offset: this.items.getOrderedItems().length,
                };

                const newItems = await this.fetchItems(paging);
                if (newItems.length < this.pageSize) {
                    this.hasMoreItems = false;
                }

                this.items.addItems({ keepExistingItems: true }, ...newItems);
                this.loadingState.setDone();
            } catch (error) {
                this.loadingState.setError();
                console.error(error);
            }
        }
    }
}
